import { Link, router } from '@inertiajs/react'
import { useState } from 'react'

// ─── Shared milestone config (same as QueueMonitorDetail) ─────────────────────
const C = { green: '#4CAF50', yellow: '#FF9800', red: '#F44336', blue: '#2196F3', gray: '#9CA3AF' }

const MILESTONE = {
  queue_activated:      { label: 'Activated',            color: '#2196F3' },
  // Order lifecycle milestones (Phase 1.5)
  order_submitted:      { label: 'Order In',             color: '#2196F3' }, // blue
  preparing:            { label: 'Preparing',            color: '#FF9800' }, // amber
  ready:                { label: 'Ready',                color: '#009688' }, // teal
  handoff_complete:     { label: 'Handoff Complete',     color: '#4CAF50' }, // green
  delivered:            { label: 'Delivered',            color: '#00C853' }, // emerald
  guest_eta_update:     { label: 'En Route',             color: '#2196F3' },
  guest_arrived:        { label: 'Arrived',              color: '#FFC107' },
  sensepoint_request:   { label: 'sensePoint Request',   color: '#FF9800' },
  sensepoint_ready:     { label: 'sensePoint Ready',     color: '#009688' },
  sensepoint_activated: { label: 'sensePoint Activated', color: '#009688' },
  sensepoint_rejected:  { label: 'sensePoint Rejected',  color: '#F44336' },
  checkin_verified:     { label: 'Check-in Verified',    color: '#009688' },
  guest_onboard:        { label: 'Guest Onboard',        color: '#00BCD4' },
  escrow_released:      { label: 'Escrow Released',      color: '#4CAF50' },
  session_closed:       { label: 'Session Closed',       color: '#607D8B' },
  mode_changed:         { label: 'Mode Changed',         color: '#607D8B' },
}

const LIFECYCLE_STEPS = [
  'queue_activated',
  'order_submitted',
  'preparing',
  'ready',
  'guest_eta_update',
  'guest_arrived',
  'sensepoint_request',    // guest taps Physical Check-in
  'sensepoint_activated',  // creator confirms
  'checkin_verified',
  'guest_onboard',
  'escrow_released',
  'handoff_complete',
  'delivered',
  'session_closed',
]

function getMilestone(key) {
  return MILESTONE[key] || { label: key?.replace(/_/g, ' ') || 'Unknown', color: C.gray }
}

function formatEta(seconds) {
  if (seconds == null) return null
  if (seconds === 0)   return 'Arrived'
  if (seconds < 60)    return `< 1 min`
  const m = Math.round(seconds / 60)
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const r = m % 60
  return r > 0 ? `${h}h ${r}m` : `${h}h`
}

function TimeAgo({ iso }) {
  if (!iso) return <span className="text-gray-400">—</span>
  const d = new Date(iso)
  return (
    <span title={d.toLocaleString()} className="text-gray-500 text-xs">
      {d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      {' · '}
      {d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
    </span>
  )
}

function SectionHeader({ children }) {
  return (
    <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 mt-6">
      {children}
    </h2>
  )
}

function StatusBadge({ status }) {
  const map = {
    completed: 'bg-green-100 text-green-800',
    cancelled:  'bg-red-100 text-red-700',
    timeout:    'bg-yellow-100 text-yellow-800',
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  )
}

function CancellationCard({ session }) {
  if (!session.cancellation_notes && !session.cancellation_message) return null
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
      <h2 className="text-xs font-bold text-red-500 uppercase tracking-widest mb-3">
        Cancellation Details
      </h2>
      {session.cancelled_by === 'admin' && (
        <p className="text-xs text-red-400 font-semibold uppercase tracking-wide mb-2">
          Admin-initiated cancellation
        </p>
      )}
      {session.cancellation_notes && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-red-700 mb-1">Reason for Cancellation</p>
          <p className="text-sm text-red-900 bg-white border border-red-200 rounded p-3 leading-relaxed">
            {session.cancellation_notes}
          </p>
        </div>
      )}
      {session.cancellation_message && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-1">Message sent to participants</p>
          <p className="text-xs text-gray-600 bg-white border border-gray-200 rounded p-3 leading-relaxed italic">
            {session.cancellation_message}
          </p>
        </div>
      )}
    </div>
  )
}

// ─── Collapse consecutive guest_eta_update events into groups ─────────────────
function groupEvents(events) {
  const groups = []
  let i = 0
  while (i < events.length) {
    if (events[i].event_type === 'guest_eta_update') {
      const etaEvents = [events[i]]
      while (i + 1 < events.length && events[i + 1].event_type === 'guest_eta_update') {
        i++
        etaEvents.push(events[i])
      }
      groups.push({ type: 'eta_group', events: etaEvents, id: `eta_${etaEvents[etaEvents.length - 1].id}` })
    } else {
      groups.push({ type: 'event', event: events[i] })
    }
    i++
  }
  return groups
}

// ─── ETA Group Row — collapsed by default ─────────────────────────────────────
function EtaGroupRow({ group }) {
  const [expanded, setExpanded] = useState(false)
  const latest   = group.events[group.events.length - 1]   // newest = last in asc order
  const oldest   = group.events[0]
  const count    = group.events.length
  const etaSec   = latest.payload?.eta_seconds
  const etaLabel = formatEta(etaSec)
  const m        = getMilestone('guest_eta_update')

  return (
    <li className="flex items-start gap-3">
      <div className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }} />
      <div className="flex-1 min-w-0">
        <button onClick={() => setExpanded(v => !v)} className="w-full text-left">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-sm flex items-center gap-2" style={{ color: m.color }}>
              {m.label}
              {count > 1 && (
                <span className="text-xs font-normal px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: m.color + '20', color: m.color }}>
                  ×{count}
                </span>
              )}
              {etaLabel && (
                <span className="text-xs font-normal" style={{ color: m.color }}>
                  Last ETA: {etaLabel}
                </span>
              )}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <TimeAgo iso={latest.created_at} />
              <span className="text-gray-300 text-xs">{expanded ? '▲' : '▼'}</span>
            </div>
          </div>
          <p className="text-xs text-gray-400 font-mono">guest_eta_update</p>
        </button>

        {expanded && (
          <ol className="mt-2 space-y-1.5 border-l-2 pl-3" style={{ borderColor: m.color + '40' }}>
            {[...group.events].reverse().map((e, idx) => {
              const sec   = e.payload?.eta_seconds
              const label = formatEta(sec)
              return (
                <li key={e.id} className="text-xs text-gray-500">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono">
                      {label ?? `${sec}s`}
                      {idx === 0 && <span className="ml-1" style={{ color: m.color }}>(latest)</span>}
                    </span>
                    <TimeAgo iso={e.created_at} />
                  </div>
                  {e.payload?.latitude != null && (
                    <p className="text-gray-400 font-mono">
                      {Number(e.payload.latitude).toFixed(5)}, {Number(e.payload.longitude).toFixed(5)}
                    </p>
                  )}
                </li>
              )
            })}
          </ol>
        )}
      </div>
      <span className="text-xs font-mono flex-shrink-0" style={{ color: C.gray }}>
        {count > 1 ? `#${oldest.event_seq}–#${latest.event_seq}` : `#${latest.event_seq}`}
      </span>
    </li>
  )
}

// ─── Single Event Row ─────────────────────────────────────────────────────────
// ─── actor_role → badge label + colour ────────────────────────────────────────
const ACTOR_BADGE = {
  guest:   { label: 'Guest',   bg: '#E3F2FD', text: '#1565C0' },
  creator: { label: 'Creator', bg: '#FFF3E0', text: '#E65100' },
  system:  { label: 'System',  bg: '#F3E5F5', text: '#6A1B9A' },
  admin:   { label: 'Admin',   bg: '#FCE4EC', text: '#880E4F' },
}
function ActorBadge({ role }) {
  if (!role) return null
  const b = ACTOR_BADGE[role] || { label: role, bg: '#F5F5F5', text: '#616161' }
  return (
    <span className="text-xs font-semibold px-1.5 py-0.5 rounded"
      style={{ backgroundColor: b.bg, color: b.text }}>
      {b.label}
    </span>
  )
}

function EventRow({ event: e }) {
  const m = getMilestone(e.event_type)
  const actorRole = e.actor_role || e.payload?.actor_role
  return (
    <li className="flex items-start gap-3">
      <div className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 font-semibold text-sm" style={{ color: m.color }}>
            {m.label}
            <ActorBadge role={actorRole} />
          </span>
          <TimeAgo iso={e.created_at} />
        </div>
        <p className="text-xs text-gray-400 font-mono">{e.event_type}</p>
        {e.payload && Object.keys(e.payload).length > 0 && (
          <details className="mt-1">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">Payload</summary>
            <pre className="text-xs bg-gray-50 rounded p-2 mt-1 overflow-x-auto text-gray-600">
              {JSON.stringify(e.payload, null, 2)}
            </pre>
          </details>
        )}
      </div>
      <span className="text-xs text-gray-300 font-mono flex-shrink-0">#{e.event_seq}</span>
    </li>
  )
}

export default function QueueHistoryDetail({
  session,
  events = [],
  messages = [],
  rails_env: railsEnv = 'production',
}) {
  const completedMilestones = new Set(events.map(e => e.event_type))
  const eventGroups = groupEvents(events)   // chronological (oldest first)

  async function handleDelete() {
    if (!window.confirm(`Permanently delete session #${session.id}? This cannot be undone.`)) return
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const res = await fetch(`/admin/queue_monitor/${session.id}?from_history=1`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
    })
    if (res.ok || res.status === 204) {
      router.visit('/admin/queue_monitor/history')
    } else {
      const body = await res.json().catch(() => ({}))
      window.alert(`Delete failed: ${body.error || res.statusText}`)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <Link href="/admin/queue_monitor/history" className="text-gray-400 hover:text-gray-700 text-sm">
              ← History
            </Link>
            <span className="text-gray-300">/</span>
            <h1 className="text-xl font-bold text-gray-900">
              Session #{session.id}
              <span className="ml-2 text-base font-normal text-gray-500">{session.offer_name}</span>
            </h1>
            <div className="ml-2">
              <StatusBadge status={session.session_status} />
            </div>
            {railsEnv === 'development' && (
              <button
                onClick={handleDelete}
                className="ml-auto text-xs text-red-500 border border-red-300 px-3 py-1 rounded hover:bg-red-50"
              >
                Delete Session
              </button>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Historical record</p>
            <p className="text-xs font-mono text-gray-500">
              {session.closed_at ? new Date(session.closed_at).toLocaleString() : '—'}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Left: metadata + lifecycle ──────────────────────────────────── */}
        <div className="lg:col-span-1 space-y-4">

          {/* Session metadata card */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <SectionHeader>Session Info</SectionHeader>
            <dl className="space-y-2 text-sm">
              {[
                ['Status',    <span key="status" className="font-semibold capitalize">{session.session_status?.replace(/_/g, ' ')}</span>],
                ['SP Mode',   <span key="mode" className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">{session.sensepoint_mode || 'cloud'}</span>],
                ['Creator',   session.creator_user_id ? `User #${session.creator_user_id}` : '—'],
                ['Guest',     session.guest_user_id   ? `User #${session.guest_user_id}`   : '—'],
                ['Offer',     `#${session.offer_id}`],
                ['Events',    session.event_count],
                ['Messages',  session.message_count],
                ['Started',   <TimeAgo key="started" iso={session.created_at} />],
                ['Activated', <TimeAgo key="activated" iso={session.activated_at} />],
                ['Closed',    <TimeAgo key="closed" iso={session.closed_at} />],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between items-start">
                  <dt className="text-gray-500 text-xs uppercase tracking-wide">{label}</dt>
                  <dd className="text-gray-800 text-right max-w-[55%]">{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <CancellationCard session={session} />

          {/* Regulatory & Disclosure */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <SectionHeader>Regulatory &amp; Disclosure</SectionHeader>
            <dl className="space-y-2 text-sm">
              {[
                ['Brand / Operator',      session.offer_brand || '—'],
                ['Offer Category',        session.offer_service_type || '—'],
                ['Disclosure Policy',     session.offer_disclosure_policy || '—'],
                ['Regulatory Framework',  session.offer_regulatory_framework || '—'],
                ['Disclosure Method',     'None (Phase 1)'],
                ['Consent Anchor',        'DWN VC — Phase 2A'],
                ['Escrow Trigger',        'XRPL EscrowFinish — Phase 3'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between items-start gap-4">
                  <dt className="text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">{label}</dt>
                  <dd className="text-gray-800 text-right text-xs">{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Lifecycle stepper */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <SectionHeader>Queue Lifecycle</SectionHeader>
            <ol className="relative space-y-0">
              {LIFECYCLE_STEPS.map((step, idx) => {
                const done   = completedMilestones.has(step)
                const m      = getMilestone(step)
                const label  = m.label
                const isLast = idx === LIFECYCLE_STEPS.length - 1

                let dotColor = '#D1D5DB'
                if (done) {
                  if (step === 'session_closed') {
                    dotColor = session.session_status === 'completed' ? C.green : C.red
                  } else if (step === 'guest_arrived') {
                    dotColor = completedMilestones.has('sensepoint_activated') ? C.green : C.yellow
                  } else if (step === 'sensepoint_request') {
                    dotColor = completedMilestones.has('sensepoint_activated')
                      ? MILESTONE.sensepoint_activated.color
                      : C.yellow
                  } else {
                    dotColor = m.color
                  }
                }

                return (
                  <li key={step} className="flex items-start gap-3 pb-4 relative">
                    {!isLast && (
                      <div className="absolute left-[11px] top-5 bottom-0 w-0.5"
                        style={{ backgroundColor: done ? dotColor + '60' : '#E5E7EB' }} />
                    )}
                    <div className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 z-10"
                      style={{
                        backgroundColor: done ? dotColor : '#F9FAFB',
                        borderColor:     done ? dotColor : '#D1D5DB',
                      }}>
                      {done && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="pt-0.5">
                      <p className="text-sm font-medium" style={{ color: done ? dotColor : '#9CA3AF' }}>
                        {label}
                      </p>
                    </div>
                  </li>
                )
              })}
            </ol>
          </div>
        </div>

        {/* ── Right: events + messages ─────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-6">

          {/* Event Feed */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <SectionHeader>Event Feed ({events.length})</SectionHeader>
              <span className="text-xs text-gray-400 mb-3">oldest first</span>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '400px', minHeight: '120px' }}>
              {events.length === 0 ? (
                <p className="text-gray-400 text-sm">No events recorded.</p>
              ) : (
                <ol className="space-y-2 pr-1">
                  {eventGroups.map((group) =>
                    group.type === 'eta_group'
                      ? <EtaGroupRow key={group.id} group={group} />
                      : <EventRow   key={group.event.id} event={group.event} />
                  )}
                </ol>
              )}
            </div>
          </div>

          {/* Status Messages */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <SectionHeader>Status Messages ({messages.length})</SectionHeader>
              <span className="text-xs text-gray-400 mb-3">oldest first</span>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '360px', minHeight: '80px' }}>
              {messages.length === 0 ? (
                <p className="text-gray-400 text-sm">No status messages.</p>
              ) : (
                <ol className="space-y-3 pr-1">
                  {messages.map(msg => {
                    const roleColors = {
                      creator: { bg: '#E94560', label: 'Creator' },
                      guest:   { bg: C.blue,   label: 'Guest' },
                      system:  { bg: '#607D8B', label: 'System' },
                    }
                    const sender    = roleColors[msg.sender_role]    || { bg: C.gray, label: msg.sender_role }
                    const recipient = roleColors[msg.recipient_role] || { bg: C.gray, label: msg.recipient_role }
                    return (
                      <li key={msg.id} className="rounded-lg border border-gray-100 p-3 bg-gray-50">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: sender.bg }}>
                              {sender.label}
                            </span>
                            <span className="text-gray-400 text-xs">→</span>
                            <span className="text-white text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: recipient.bg }}>
                              {recipient.label}
                            </span>
                            <span className="text-xs font-mono text-gray-600 bg-white border border-gray-200 px-1.5 py-0.5 rounded">
                              {msg.message_type}
                            </span>
                            {msg.read_at && <span className="text-xs" style={{ color: C.green }}>✓ read</span>}
                          </div>
                          <TimeAgo iso={msg.created_at} />
                        </div>
                        {msg.payload && Object.keys(msg.payload).length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">Payload</summary>
                            <pre className="text-xs bg-white rounded border border-gray-200 p-2 mt-1 overflow-x-auto text-gray-600">
                              {JSON.stringify(msg.payload, null, 2)}
                            </pre>
                          </details>
                        )}
                      </li>
                    )
                  })}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
